import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Library, X, ShieldCheck } from "lucide-react";
import ComplianceView from "@/pages/ComplianceView";

const levelBadge = {
  ALTA: "bg-destructive/10 text-destructive", MEDIA: "bg-[#F59E0B]/10 text-[#B45309]", BASSA: "bg-[#00A859]/10 text-[#00784A]",
};
const emptyForm = { title: "", category: "", trigger: "", problem: "", recommendation: "", priority: "MEDIA", effort: "MEDIO", recommended_tools: [{ name: "", note: "" }] };

export default function RemediationLibrary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [view, setView] = useState("ops");

  const load = async () => { const { data } = await api.get("/remediation-library"); setItems(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ title: r.title, category: r.category || "", trigger: r.trigger || "", problem: r.problem || "", recommendation: r.recommendation || "", priority: r.priority || "MEDIA", effort: r.effort || "MEDIO", recommended_tools: r.recommended_tools?.length ? r.recommended_tools : [{ name: "", note: "" }] });
    setEditId(r.id); setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titolo obbligatorio"); return; }
    const payload = { ...form, recommended_tools: form.recommended_tools.filter((t) => t.name.trim()) };
    try {
      if (editId) await api.put(`/remediation-library/${editId}`, payload);
      else await api.post("/remediation-library", payload);
      toast.success("Voce salvata"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (rid) => {
    if (!window.confirm("Eliminare questa voce?")) return;
    await api.delete(`/remediation-library/${rid}`); toast.success("Eliminata"); load();
  };

  const updTool = (i, patch) => setForm((f) => ({ ...f, recommended_tools: f.recommended_tools.map((t, idx) => idx === i ? { ...t, ...patch } : t) }));
  const addTool = () => setForm((f) => ({ ...f, recommended_tools: [...f.recommended_tools, { name: "", note: "" }] }));
  const rmTool = (i) => setForm((f) => ({ ...f, recommended_tools: f.recommended_tools.filter((_, idx) => idx !== i) }));

  return (
    <Layout>
      <PageHeader title="Libreria Remediation" subtitle={view === "ops" ? `${items.length} soluzioni consigliate` : "Cybersecurity & Compliance"}>
        {view === "ops" && <Button data-testid="add-library-btn" onClick={openNew} className="rounded-none"><Plus className="w-4 h-4 mr-2" /> Nuova voce</Button>}
      </PageHeader>

      <div className="px-4 sm:px-8 pt-4">
        <div className="flex gap-1 border-b border-border">
          <button data-testid="tab-ops" onClick={() => setView("ops")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 ${view === "ops" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Library className="w-4 h-4 mr-1.5 inline-block" /> Operativa
          </button>
          <button data-testid="tab-compliance" onClick={() => setView("compliance")}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 ${view === "compliance" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <ShieldCheck className="w-4 h-4 mr-1.5 inline-block" /> Cybersecurity &amp; Compliance
          </button>
        </div>
      </div>

      {view === "compliance" ? (
        <div className="p-4 sm:p-8"><ComplianceView /></div>
      ) : (
      <div className="p-4 sm:p-8">
        {loading ? <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-list">
            {items.map((r, i) => (
              <div key={r.id} data-testid={`library-card-${i}`} className="bg-white border border-border p-5">
                <div className="flex items-start justify-between">
                  <Library className="w-5 h-5 text-primary" />
                  <span className={`px-2 py-0.5 text-[10px] font-bold ${levelBadge[r.priority] || "bg-secondary"}`}>{r.priority}</span>
                </div>
                <h3 className="font-heading font-semibold tracking-tight mt-3">{r.title}</h3>
                {r.category && <div className="data-label mt-1">{r.category}</div>}
                {r.trigger && <p className="text-xs text-muted-foreground mt-1 italic">Trigger: {r.trigger}</p>}
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{r.recommendation}</p>
                {r.recommended_tools?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {r.recommended_tools.map((t, j) => <span key={j} className="text-[10px] font-mono bg-secondary px-2 py-0.5" title={t.note}>{t.name}</span>)}
                  </div>
                )}
                <div className="flex items-center gap-3 mt-4 pt-4 border-t border-border">
                  <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-primary" data-testid={`edit-library-${i}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-library-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading tracking-tight">{editId ? "Modifica voce" : "Nuova voce libreria"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="data-label">Titolo *</Label><Input data-testid="library-title-input" className="mt-1 rounded-none" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="data-label">Categoria</Label><Input className="mt-1 rounded-none" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="es. CRM" /></div>
              <div><Label className="data-label">Trigger</Label><Input className="mt-1 rounded-none" value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} placeholder="es. Nessun CRM" /></div>
              <div>
                <Label className="data-label">Priorità</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{["ALTA", "MEDIA", "BASSA"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="data-label">Effort</Label>
                <Select value={form.effort} onValueChange={(v) => setForm({ ...form, effort: v })}>
                  <SelectTrigger className="mt-1 rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">{["BASSO", "MEDIO", "ALTO"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="data-label">Raccomandazione</Label><Textarea className="mt-1 rounded-none" rows={2} value={form.recommendation} onChange={(e) => setForm({ ...form, recommendation: e.target.value })} /></div>
            <div>
              <Label className="data-label">Tool/software consigliati</Label>
              <div className="space-y-2 mt-1">
                {form.recommended_tools.map((t, i) => (
                  <div key={i} className="flex gap-2">
                    <Input className="rounded-none h-8 text-sm" value={t.name} onChange={(e) => updTool(i, { name: e.target.value })} placeholder="Nome tool" />
                    <Input className="rounded-none h-8 text-sm" value={t.note} onChange={(e) => updTool(i, { note: e.target.value })} placeholder="Nota" />
                    {form.recommended_tools.length > 1 && <button onClick={() => rmTool(i)} className="text-muted-foreground hover:text-destructive"><X className="w-4 h-4" /></button>}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="rounded-none" onClick={addTool}><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi tool</Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-library-btn" className="rounded-none" onClick={save}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
